import { Node, mergeAttributes } from '@tiptap/core';

export const Gate = Node.create({
  name: 'gate',
  group: 'block',
  atom: true,

  addOptions() {
    return {
      templates: null, // { editor_html: string } — injected by tiptap controller from server template
    };
  },

  addAttributes() {
    return {
      gatePlanIds: {
        default: '',
        parseHTML: el => el.getAttribute('data-gate-plan-ids') || '',
        renderHTML: attrs => ({ 'data-gate-plan-ids': attrs.gatePlanIds }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-gate]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-gate': '' }, HTMLAttributes)];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const extension = this;

      const dom = document.createElement('div');
      const editorHtml = extension.options.templates?.editor_html;
      dom.innerHTML = editorHtml || '<div class="border-2 border-dashed border-indigo-300 rounded p-3 text-sm text-indigo-600">GATE BELOW</div>';

      // Remove gate button
      dom.querySelector('[data-remove]')?.addEventListener('mousedown', e => {
        e.preventDefault();
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos !== null) {
          const current = editor.state.doc.nodeAt(pos);
          if (current) {
            editor.chain().focus().deleteRange({ from: pos, to: pos + current.nodeSize }).run();
          }
        }
      });

      // When TargetingComponent changes, sync plan IDs back to Tiptap attrs. Gates target plans
      // only (no billing interval), so a data-gate-price-interval on an older gate isn't parsed
      // and drops off on the next save. TargetingComponent's Stimulus controller fires change
      // events and stores values in planSelectTarget (via TomSelect).
      dom.addEventListener('change', () => {
        const planSelectEl = dom.querySelector('[data-targeting-component-target="planSelect"]');

        const planIds = planSelectEl?.tomselect
          ? planSelectEl.tomselect.getValue()  // TomSelect returns comma-separated string for multi
          : '';
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos !== null) {
          editor.chain().setNodeSelection(pos)
            .updateAttributes('gate', { gatePlanIds: planIds })
            .run();
        }
      });

      // After TargetingComponent's Stimulus controller initialises TomSelect, set initial
      // values from the node's existing attrs (needed when loading a post that already has a gate).
      if (node.attrs.gatePlanIds) {
        const trySetInitialValues = () => {
          const planSelectEl = dom.querySelector('[data-targeting-component-target="planSelect"]');
          if (!planSelectEl?.tomselect) return false;

          const ids = node.attrs.gatePlanIds.split(',').filter(Boolean);
          if (ids.length) {
            // Switch to "targeted" radio so TargetingComponent shows the selects
            const targetedRadio = dom.querySelector('input[type="radio"][value="targeted"]');
            if (targetedRadio && !targetedRadio.checked) {
              targetedRadio.click();
            }
            planSelectEl.tomselect.setValue(ids, true /* silent */);
          }
          return true;
        };

        // TomSelect init is async (fetchPlans is awaited). Poll briefly via MutationObserver.
        const observer = new MutationObserver(() => {
          if (trySetInitialValues()) observer.disconnect();
        });
        observer.observe(dom, { childList: true, subtree: true });
        // Fallback disconnect after 5 s to avoid leaks
        setTimeout(() => observer.disconnect(), 5000);
      }

      return {
        dom,
        update(updatedNode) {
          // Must return true to prevent Tiptap from destroying and recreating the
          // nodeview on every attr change (which would reset TomSelect's selection).
          if (updatedNode.type.name !== 'gate') return false;
          return true;
        },
      };
    };
  },

  addCommands() {
    return {
      insertGate: (attrs = {}) => ({ chain }) =>
        chain().insertContent({ type: this.name, attrs }).run(),
    };
  },
});

export const gateSlashItem = {
  title: 'Gating divider',
  description: 'Lock everything below behind a subscription',
  icon: '🔒',
  group: 'Blocks',
  keywords: ['gate', 'lock', 'paywall', 'members', 'subscribers', 'paid'],
  canInsert: (editor) => {
    let hasGate = false;
    editor.state.doc.descendants(node => { if (node.type.name === 'gate') hasGate = true; });
    return !hasGate;
  },
  command: ({ editor, range }) => {
    editor.chain().focus().deleteRange(range).insertGate().run();
  },
};
